import moment from 'moment'

import { getIdOrNull, getTrimmedOrNull, isNonEmptyArray, validateString } from '..'
import { Properties } from '../entities'
import { API_DATE, DAY, getTimezoneLessMoment } from '../momentjs'
import { EUROPEAN_DATE } from '../constants'
import { RequestPeriod } from './requestPeriod'
import { School } from './school'

export const States = Object.freeze({
  PENDING: 'PENDING',
  PARTIALLY_ACCEPTED: 'PARTIALLY_ACCEPTED',
  ACCEPTED: 'ACCEPTED',
  REFUSED: 'REFUSED'
})

export const Tags = Object.freeze({
  PENDING: { label: 'school_portal_request.status.pending', color: 'blue' },
  PARTIALLY_ACCEPTED: { label: 'school_portal_request.status.partially_accepted', color: 'orange' },
  ACCEPTED: { label: 'school_portal_request.status.accepted', color: 'green' },
  REFUSED: { label: 'school_portal_request.status.refused', color: 'gray' }
})

const UNKNOWN_STATE_TAG = Object.freeze({ label: 'school_portal_request.status.unknown', color: 'gray' })

export const getStateTag = state => Tags[state] ?? UNKNOWN_STATE_TAG

export const CoordinatorValidators = Object.freeze({
  [Properties.FIRST_NAME]: c => validateString(c[Properties.FIRST_NAME], { notEmpty: true }),
  [Properties.LAST_NAME]: c => validateString(c[Properties.LAST_NAME], { notEmpty: true }),
  [Properties.EMAIL]: c => validateString(c[Properties.EMAIL], { isEmail: true, notEmpty: true }),
  [Properties.JOB_TITLE]: c => validateString(c[Properties.JOB_TITLE], { notEmpty: true })
})

export const getCoordinatorFullname = c => [c?.firstName, c?.lastName].filter(part => (part ?? '') !== '').join(' ')

export const getSchoolName = r => r?.school?.name ?? null
export const getCreatedAt = r => r?.createdAt ? getTimezoneLessMoment(r.createdAt).format(EUROPEAN_DATE) : null

const periodSignature = period => {
  const startDate = getTimezoneLessMoment(period?.startDate)
  const endDate = getTimezoneLessMoment(period?.endDate)

  return `${startDate?.format(API_DATE) ?? ''}_${endDate?.format(API_DATE) ?? ''}`
}

export const groupRequests = requests => {
  const groups = new Map()

  ;(requests ?? []).forEach(request => {
    const key = [
      getIdOrNull(request.sectionToYear),
      getIdOrNull(request.sector),
      (request.requestPeriods ?? []).map(periodSignature).join(',')
    ].join('|')

    if (!groups.has(key)) {
      groups.set(key, { key, sample: request, requests: [] })
    }

    groups.get(key).requests.push(request)
  })

  return [...groups.values()]
}

export const summarizeGrantedPeriods = requests => {
  const byPeriod = new Map()

  ;(requests ?? []).forEach(request => (request.grantedPeriods ?? []).forEach(period => {
    const signature = periodSignature(period)

    byPeriod.set(signature, { ...period, signature, places: (byPeriod.get(signature)?.places ?? 0) + 1 })
  }))

  return [...byPeriod.values()].sort((a, b) => a.signature.localeCompare(b.signature))
}

export const countGrantedPlaces = requests => (requests ?? [])
  .reduce((total, request) => total + (request.grantedPeriods ?? []).length, 0)

export const ExpiryFilters = Object.freeze({ OPEN: 'OPEN', EXPIRED: 'EXPIRED' })

export const isPeriodStillOpen = period => {
  const startDate = getTimezoneLessMoment(period?.startDate)

  return startDate !== null && startDate.isAfter(moment(), DAY)
}

export const hasOpenPeriod = request => (request?.requestPeriods ?? []).some(isPeriodStillOpen)

export const isExpired = r => r?.state === States.PENDING &&
  isNonEmptyArray(r?.requests) &&
  !r.requests.some(hasOpenPeriod)

export const countLines = r => groupRequests(r?.requests).length
export const countPlaces = r => isNonEmptyArray(r?.requests) ? r.requests.length : 0

const distinct = (requests, getter) => [...new Set(
  (requests ?? []).map(getter).filter(value => (value ?? '') !== '')
)]

export const getSectionsLabel = r => distinct(r?.requests, line => line.sectionToYear?.sectionAbbreviation ?? line.section?.abbreviation).join(', ')
export const getSectorsLabel = r => distinct(r?.requests, line => line.sector?.name).join(', ')

const MergeTags = Object.freeze({
  JOINS_EXISTING: { label: 'school_portal_request.merge.joins_existing', color: 'gold' },
  HAS_TWINS: { label: 'school_portal_request.merge.has_twins', color: 'gold' }
})

export const joinsExistingSchool = r => r?.schoolAlreadyKnown === true
export const countTwinRequests = r => r?.twinPendingCount ?? 0

export const getMergeTag = r => {
  if (joinsExistingSchool(r)) {
    return MergeTags.JOINS_EXISTING
  }

  return countTwinRequests(r) > 0 ? MergeTags.HAS_TWINS : null
}

const CLOSED_STATES = Object.freeze([States.PARTIALLY_ACCEPTED, States.ACCEPTED, States.REFUSED])

export const isClosed = r => CLOSED_STATES.includes(r?.state)

export class PortalCoordinator {
  constructor ({ firstName, lastName, email, phone, jobTitle, languageLocale } = {}) {
    this.firstName = firstName ?? ''
    this.lastName = lastName ?? ''
    this.email = email ?? ''
    this.phone = phone ?? ''
    this.jobTitle = jobTitle ?? ''
    this.languageLocale = languageLocale ?? null
  }

  toRequestBody = () => ({
    firstName: getTrimmedOrNull(this.firstName),
    lastName: getTrimmedOrNull(this.lastName),
    email: getTrimmedOrNull(this.email),
    phone: getTrimmedOrNull(this.phone),
    jobTitle: getTrimmedOrNull(this.jobTitle),
    languageLocale: this.languageLocale
  })
}

export class SchoolPortalRequestLine {
  constructor ({
    id, section, sectionToYear, institution, sector, hospitalDepartment, requestPeriods, grantedPeriods, total, duration, comment, state
  } = {}) {
    this.id = id ?? -1
    this.section = section ?? null
    this.sectionToYear = sectionToYear ?? null
    this.institution = institution ?? null
    this.sector = sector ?? null
    this.hospitalDepartment = hospitalDepartment ?? null
    this.requestPeriods = isNonEmptyArray(requestPeriods) ? requestPeriods.map(p => new RequestPeriod(p)) : []
    this.grantedPeriods = isNonEmptyArray(grantedPeriods) ? grantedPeriods.map(p => new RequestPeriod(p)) : []
    this.total = total ?? 1
    this.duration = duration ?? null
    this.comment = comment ?? ''
    this.state = state ?? States.PENDING
  }

  toRequestBody = () => ({
    section: getIdOrNull(this.section),
    sectionToYear: getIdOrNull(this.sectionToYear),
    institution: getIdOrNull(this.institution),
    sector: getIdOrNull(this.sector),
    requestPeriods: this.requestPeriods.map(period => new RequestPeriod(period).toRequestBody()),
    total: this.total,
    duration: this.duration,
    comment: getTrimmedOrNull(this.comment)
  })
}

export class SchoolPortalRequest {
  constructor ({ id, coordinator, school, requests, state, createdAt, refusalReason, schoolAlreadyKnown, twinPendingCount } = {}) {
    this.id = id ?? -1
    this.coordinator = new PortalCoordinator(coordinator)
    this.school = new School(school)
    this.requests = isNonEmptyArray(requests) ? requests.map(r => new SchoolPortalRequestLine(r)) : []
    this.state = state ?? States.PENDING
    this.createdAt = getTimezoneLessMoment(createdAt) ?? moment()
    this.refusalReason = refusalReason ?? null
    this.schoolAlreadyKnown = schoolAlreadyKnown ?? false
    this.twinPendingCount = twinPendingCount ?? 0
  }

  toRequestBody = () => ({
    coordinator: this.coordinator.toRequestBody(),
    school: this.school.toRequestBody(),
    requests: this.requests.map(request => request.toRequestBody())
  })
}
